import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate, Location } from 'react-router';

import { AuthShell } from '@/components/auth/AuthShell';
import { LoadingIndicator } from '@/components/common/LoadingIndicator';
import { Button } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useTranslationPrefix } from '@/translations/i18n';

export interface PasswordResetRequestSentLocationState {
  email?: string;
}

const PasswordResetRequestSent: React.FC = () => {
  const t = useTranslationPrefix('reset_password.request_sent');
  const location = useLocation() as Location<PasswordResetRequestSentLocationState | undefined>;
  const navigate = useNavigate();
  const { resetPassword, isResettingPassword } = useAuthContext();

  const [error, setError] = useState<string | null>(null);

  // DEV ONLY: preview without navigating from password reset request flow
  const DEV_MOCK = import.meta.env.MODE === 'development' && !location.state?.email?.trim();
  const email = DEV_MOCK ? 'preview@hauserhq.com' : location.state?.email?.trim();

  if (!email) {
    return <Navigate to="/login" />;
  }

  const onLoginClick = () => {
    navigate('/login');
  };

  const onResendPasswordResetClick = async () => {
    try {
      setError(null);
      await resetPassword(email);
    } catch (error: any) {
      setError(error.message);
    }
  };

  return (
    <AuthShell
      title="Check your email"
      footer={
        isResettingPassword ? (
          <LoadingIndicator size={16} sx={{ color: 'rgba(247,247,246,0.6)' }} />
        ) : (
          <>
            {t('did_not_receive_email')}
            <button type="button" className="hx-auth-link" onClick={onResendPasswordResetClick}>
              {t('resend_email')}
            </button>
          </>
        )
      }>
      <h1 className="hx-auth-title">{t('title')}</h1>
      <p className="hx-auth-sub">
        {t('intro.0')}
        <span style={{ color: '#f7f7f6', fontWeight: 600 }}>{email}</span>. {t('intro.1')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <p className="hx-auth-error">{error}</p>}
        <Button variant="primary" block disabled={isResettingPassword} onClick={onLoginClick}>
          {t('log_in')}
        </Button>
      </div>
    </AuthShell>
  );
};

export default PasswordResetRequestSent;
